import EventBus from '../../wirebox/event-bus.js'
import jQuery from 'jquery'
import GoogleLoaderModule from './loader.js'

class GoogleMapsModule {
    
    /**
     * Construct module
    */
    constructor() {
        
        /**
         * Whether Google Maps has been loaded
         * @type {boolean}
        */
        this.isLoaded = false
        
        /**
         * Whether Google Maps is being loaded
         * @type {boolean}
        */
        this.isLoading = false
        
        /**
         * Callbacks to execute once the Maps API is loaded
         * @type {array}
        */
        this.callbacks = []
        
        /**
         * @type {object}
        */
        this.libraryHash = {}
        
        /**
         * @type {array}
        */
        this.libraries = []
        
        /**
         * @type string|null
        */
        this.language = (typeof googleLanguage === 'string' ? googleLanguage : null)
        
        this.bindEvents()
        
    }
    
    /**
     * Load Google Maps
     * @param callback
    */
    loadGoogleMaps(callback) {
        
        //Callback if Maps API is already loaded
        if (this.isLoaded) {
            callback()
            return
        }
        
        //Add callback to callbacks list
        this.callbacks.push(callback)
        
        //Cancel if API is being loaded
        if (this.isLoading) {
            return
        }
        
        //Set that the API is being loaded
        this.isLoading = true
        
        //Generate libraries string
        var libraries = ''
        jQuery.each(this.libraries, (i, library) => {
            libraries += (i > 0 ? ',' : '') + library
        })
        
        jQuery.getScript(
            'https://maps.googleapis.com/maps/api/js?callback=Function.prototype&key=' + gMapsApiKey + (typeof gmapsApiParams == 'string' ? gmapsApiParams : '') + (libraries ? ('&libraries=' + libraries) : '') + (this.language ? ('&language=' + this.language) : ''),
            () => {
                
                //Set that the Maps API has been loaded
                this.isLoaded = true
                this.isLoading = false
                
                //Execute callbacks now that the API is loaded
                jQuery.each(this.callbacks, (i, cb) => {
                    cb()
                })
                
            }
        )
        
    }
    
    /**
     * Map container initialised
     * @param element
    */
    mapContainerInitialised(element) {
        element.addClass('initialised')
    }
    
    /**
     * Construct Google map
     * @param params
    */
    constructGoogleMap({callback, center, element, options}) {

        let
            mapCenter = new google.maps.LatLng(center.latitude, center.longitude),
            defaultOptions = {
                center:             mapCenter,
                zoom:               10,
                zoomControl:        true,
                streetViewControl:  false,
                mapTypeControl:     false,
                scrollwheel:        false
            },
            mapObject = new google.maps.Map(element, jQuery.extend(defaultOptions, options))
        

        callback({
            center: mapCenter,
            map:    mapObject
        })
        
    }
    
    /**
     * Construct map icon
     * @param params
    */
    constructMapIcon(params) {
        params.callback({
            url: params.src,
            size: new google.maps.Size(params.width, params.height),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(params.scaledWidth, params.scaledHeight),
            scaledSize: new google.maps.Size(params.scaledWidth, params.scaledHeight)
        })
    }
    
    /**
     * Construct map markers
     * @param params
    */
    constructMapMarkers(params) {
        
        var markers = {}
        
        let
            marker,
            position
        
        jQuery.each(params.positions, (i, coordinate) => {
            position = new google.maps.LatLng(coordinate.latitude, coordinate.longitude)
            marker = this.createMarkerObject(coordinate.title, position, coordinate.hasOwnProperty('icon') ? coordinate.icon : params.hasOwnProperty('icon') ? params.icon : null, params.map)
            marker.id = coordinate.id

            if (typeof coordinate.infobox != 'undefined') {
              marker.infowindow = new google.maps.InfoWindow({
                content: coordinate.infobox
              })

              google.maps.event.addListener(marker, 'mouseover', function () {
                this['infowindow'].open(params.map, this)
              })
            }

            markers[coordinate.id] = marker
        })
        
        if (typeof params.callback == 'function') {
            params.callback(markers)
        }
        
    }
    
    /**
     * Return map marker object
     * @param title
     * @param position
     * @param icon
     * @param map
    */
    createMarkerObject(title, position, icon, map) {
        return new google.maps.Marker({
            title: title,
            position: position,
            icon: icon,
            animation: google.maps.Animation.DROP,
            map: map
        })
    }
    
    /**
     * Refresh map
     * @param params
    */
    refreshMap(params) {
        google.maps.event.trigger(params.map, 'resize')
        this.centerMap(params)
    }
    
    /**
     * Center map
     * @param params
    */
    centerMap(params) {
        params.map.setCenter(params.center)
    }
    
    /**
     * Zoom in map
     * @param map
    */
    zoomInMap(map) {
        map.setZoom(map.getZoom() + 1)
    }
    
    /**
     * Zoom out map
     * @param map
    */
    zoomOutMap(map) {
        map.setZoom(map.getZoom() - 1)
    }
    
    /**
     * Remove map markers
     * @param markers
    */
    removeMapMarkers(markers) {
        jQuery.each(markers, (i, marker) => {
            marker.setMap(null)
        })
    }
    
    /**
     * Show map markers
     * @param params
    */
    showMapMarkers(params) {
        jQuery.each(params.markers, (i, marker) => {
            marker.setMap(params.map)
        })
    }
    
    /**
     * Show map markers
     * @param params
    */
    bindMarkerClicks(params) {
        jQuery.each(params.markers, (i, marker) => {
            google.maps.event.addListener(marker, 'click', params.callback)
        })
    }
    
    /**
     * Update center of the map to new coordinates
     * @param params
     */
    updateMapCoordinates(params) {
        
        //Get coordinates object
        let center = new google.maps.LatLng(params.coordinates.latitude, params.coordinates.longitude)
        
        //Apply coordinates to map
        this.centerMap({
            center: center,
            map: params.map
        })
        
    }
    
    /**
     * Request an additional library be loaded to maps
     * @param library
    */
    addLibrary(library) {
        if (!this.libraryHash.hasOwnProperty(library)) {
            this.libraries.push(library)
            this.libraryHash[library] = true
        }
    }
    
    /**
     * Request that Google Places be loaded as a library
    */
    loadGooglePlaces() {
        this.addLibrary('places')
    }
    
    /**
     * Setup autocomplete input
     * @params
    */
    setupAutoComplete(params) {
        
        let
            /*circle = new google.maps.Circle({
                center: {
                    lat: params.coordinates.latitude,
                    lng: params.coordinates.longitude
                },
                radius: params.coordinates.accuracy
            }),*/
            autocomplete = new google.maps.places.Autocomplete(params.input, params.options)
        
        autocomplete.bindTo('bounds', params.map)
        autocomplete.addListener('place_changed', params.change)

        params.callback(autocomplete)
        
    }
    
    /**
     * Pan map to location
     * @param params
    */
    panMapTo(params) {
        params.map.panTo(params.location)
    }
    
    /**
     * Set map zoom
     * @param params
    */
    setMapZoom(params) {
        params.map.setZoom(params.zoom)
    }
    
    /**
     * Event subscription
     * @param event
     * @param callback
    */
    subscribe(event, callback) {
        EventBus.subscribe(event, callback.bind(this))
        return this
    }
    
    /**
     * Bind events
    */
    bindEvents() {
        this
            .subscribe('load-google-maps', this.loadGoogleMaps)
            .subscribe('map-container-initialised', this.mapContainerInitialised)
            .subscribe('construct-google-map', this.constructGoogleMap)
            .subscribe('refresh-google-map', this.refreshMap)
            .subscribe('center-google-map', this.centerMap)
            .subscribe('zoom-in-map', this.zoomInMap)
            .subscribe('zoom-out-map', this.zoomOutMap)
            .subscribe('construct-map-icon', this.constructMapIcon)
            .subscribe('construct-map-markers', this.constructMapMarkers)
            .subscribe('remove-map-markers', this.removeMapMarkers)
            .subscribe('show-map-markers', this.showMapMarkers)
            .subscribe('bind-marker-clicks', this.bindMarkerClicks)
            .subscribe('update-map-coordinates', this.updateMapCoordinates)
            .subscribe('load-google-places-library', this.loadGooglePlaces)
            .subscribe('setup-map-autocomplete', this.setupAutoComplete)
            .subscribe('pan-map-to', this.panMapTo)
            .subscribe('set-map-zoom', this.setMapZoom)
    }
    
}

EventBus.subscribe('init-modules',  () => {
    new GoogleMapsModule()
})

export default GoogleMapsModule
